import * as React from "react";

import { cn } from "../../lib/utils";

/** Platform-aware keyboard hint glyph — one cap per key, used in tooltips, menus and the palette. */

type KbdSize = "sm" | "default";

export interface KbdProps extends Omit<
  React.HTMLAttributes<HTMLElement>,
  "children"
> {
  /** A single key ("mod+k", "Enter") or an explicit list (["mod", "K"]). */
  keys: string | string[];
  /** `sm` = 16px caps (inside tooltips / footers), `default` = 20px caps. */
  size?: KbdSize;
}

const capSizes: Record<KbdSize, string> = {
  sm: "h-4 min-w-4 px-1 text-xs",
  default: "h-5 min-w-5 px-1 text-xs",
};

/** True on Apple platforms, where `mod` renders as ⌘ rather than Ctrl. */
function isApplePlatform(): boolean {
  if (typeof navigator === "undefined") return false;
  const platform =
    (navigator as Navigator & { userAgentData?: { platform?: string } })
      .userAgentData?.platform ??
    navigator.platform ??
    navigator.userAgent ??
    "";
  return /mac|iphone|ipad|ipod/i.test(platform);
}

const SHARED_GLYPHS: Record<string, string> = {
  shift: "⇧",
  alt: "⌥",
  option: "⌥",
  enter: "↵",
  return: "↵",
  esc: "Esc",
  escape: "Esc",
  up: "↑",
  arrowup: "↑",
  down: "↓",
  arrowdown: "↓",
  left: "←",
  arrowleft: "←",
  right: "→",
  arrowright: "→",
  backspace: "⌫",
  delete: "⌦",
  tab: "⇥",
  space: "Space",
  plus: "+",
};

const APPLE_GLYPHS: Record<string, string> = {
  mod: "⌘",
  cmd: "⌘",
  command: "⌘",
  meta: "⌘",
  ctrl: "⌃",
  control: "⌃",
};

const NON_APPLE_GLYPHS: Record<string, string> = {
  mod: "Ctrl",
  cmd: "Ctrl",
  command: "Ctrl",
  meta: "Ctrl",
  ctrl: "Ctrl",
  control: "Ctrl",
};

/** Human-readable name for each cap, used to build the accessible label. */
const SPOKEN: Record<string, string> = {
  "⌘": "Command",
  "⌃": "Control",
  "⇧": "Shift",
  "⌥": "Alt",
  "↵": "Enter",
  "⌫": "Backspace",
  "⌦": "Delete",
  "⇥": "Tab",
  "↑": "Arrow up",
  "↓": "Arrow down",
  "←": "Arrow left",
  "→": "Arrow right",
};

function normaliseKeys(keys: string | string[]): string[] {
  const list = Array.isArray(keys) ? keys : [keys];
  return list
    .flatMap((key) =>
      // Allow "mod+shift+k" shorthand, but keep a literal "+" key intact.
      key.length > 1 ? key.split("+") : [key],
    )
    .map((key) => key.trim())
    .filter((key) => key.length > 0);
}

function renderKey(key: string, apple: boolean): string {
  const lookup = key.toLowerCase();
  const platformGlyphs = apple ? APPLE_GLYPHS : NON_APPLE_GLYPHS;
  const glyph = platformGlyphs[lookup] ?? SHARED_GLYPHS[lookup];
  if (glyph) return glyph;
  return key.length === 1 ? key.toUpperCase() : key;
}

const Kbd = React.forwardRef<HTMLElement, KbdProps>(
  ({ keys, size = "default", className, ...props }, ref) => {
    const apple = React.useMemo(isApplePlatform, []);
    const caps = React.useMemo(
      () => normaliseKeys(keys).map((key) => renderKey(key, apple)),
      [keys, apple],
    );

    const spoken = caps.map((cap) => SPOKEN[cap] ?? cap).join(" ");

    return (
      <kbd
        ref={ref}
        data-slot="kbd"
        role="img"
        aria-label={spoken}
        className={cn("inline-flex items-center gap-0.5", className)}
        {...props}
      >
        {caps.map((cap, index) => (
          <span
            key={`${cap}-${index}`}
            aria-hidden="true"
            className={cn(
              "inline-flex items-center justify-center rounded-sm",
              "bg-hover font-mono font-medium leading-none text-text-tertiary",
              capSizes[size],
            )}
          >
            {cap}
          </span>
        ))}
      </kbd>
    );
  },
);
Kbd.displayName = "Kbd";

export { Kbd };
